import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormApi, FORM_ERROR } from 'final-form';
import { CircularProgress } from '@material-ui/core';
import MuiButton from '@material-ui/core/Button';
import * as yup from 'yup';
import Form from 'ui/Form/Form';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import Button, { Themes as ButtonThemes } from 'ui/Button/Button';
import { DigitInput } from 'ui/Input';
import noop from 'utils/noop';
import TimerCountDown from 'utils/timer';
import { getMinutes, getSeconds } from 'utils/formatters';
import { requiredOnly } from 'utils/validation';
import { NETWORK_REQUIRED } from 'utils/constants';
import { IOTPModalProps } from '../IModalProps';
import ModalWindow from '../ModalWindow/ModalWindow';

import styles from './OTPVerificationModal.module.css';

interface IOtpPin {
  digit1: string;
  digit2: string;
  digit3: string;
  digit4: string;
  digit5: string;
  // digit6: string;
}

export type FormType = FormApi<IOtpPin, Partial<IOtpPin>>;

type Touched =
  | {
      [key: string]: boolean;
    }
  | undefined;

const initialValues: IOtpPin = {
  digit1: '',
  digit2: '',
  digit3: '',
  digit4: '',
  digit5: '',
  // digit6: '',
};

const schema = yup.object().shape({
  digit1: requiredOnly('OTP'),
  digit2: requiredOnly('OTP'),
  digit3: requiredOnly('OTP'),
  digit4: requiredOnly('OTP'),
  digit5: requiredOnly('OTP'),
  // digit6: requiredOnly('OTP'),
});

const OTPVerificationModal = ({
  open,
  onClose,
  onSubmit,
  onResend,
  OTPModalError,
  loading,
}: IOTPModalProps & { loading?: boolean }) => {
  const timer = useMemo(() => new TimerCountDown(), []);
  const [timeLeft, setTimeLeft] = useState<number | undefined>();
  const [resending, setResending] = useState(false);
  const digitRefs = [
    useRef<HTMLInputElement>(),
    useRef<HTMLInputElement>(),
    useRef<HTMLInputElement>(),
    useRef<HTMLInputElement>(),
    useRef<HTMLInputElement>(),
    // useRef<HTMLInputElement>(),
  ];

  const requestRef = useRef<number | undefined>();

  const animate: FrameRequestCallback = () => {
    setTimeLeft(timer.time);
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (!open) return;

    setTimeout(() => {
      digitRefs.forEach((ref, index) => {
        const input = ref.current;
        if (!input) return;

        input.addEventListener('input', () => {
          if (input.value.length < 1) return;

          const nextInput = digitRefs[index + 1]?.current;
          if (!nextInput) return;
          nextInput.focus();
          nextInput.select();
        });
      });
    }, 200);
  }, [open]);

  const doSubmit = useCallback(
    ({ digit1, digit2, digit3, digit4, digit5 }: IOtpPin, form: FormType) => {
      const validated = !form.getState().hasValidationErrors;
      if (onSubmit && validated) {
        const otp = digit1 + digit2 + digit3 + digit4 + digit5;
        return onSubmit(otp, form);
      }
      return undefined;
    },
    [onSubmit],
  );

  const stopTimer = useCallback(() => {
    timer.stop();
    if (requestRef.current != null) {
      cancelAnimationFrame(requestRef.current);
    }
  }, [timer]);

  const handleOnClose = () => {
    stopTimer();
    onClose();
  };

  const onResendOtp = useCallback(
    (form: FormType) => () => {
      setResending(true);
      onResend()
        .then(() => {
          if (open) {
            timer.start(2 * 60);
            form.reset();
          } else {
            stopTimer();
          }
        })
        .catch((error) => {
          console.error(error || NETWORK_REQUIRED);
        })
        .finally(() => setResending(false));
    },
    [onResend, open, stopTimer, timer],
  );

  const timeIsOver = useMemo(() => timeLeft != null && timeLeft <= 0, [timeLeft]);

  const handleOnResendOtp = useCallback(
    (form: FormType) => (timeIsOver ? onResendOtp(form) : noop),
    [onResendOtp, timeIsOver],
  );

  useEffect(() => {
    const { failureCount, description } = OTPModalError || {};
    if (failureCount === 0 || description === 'OTP validation: Otp is nullified') {
      setResending(true);
      onResend()
        .then(() => {
          if (open) {
            timer.start(2 * 60);
          } else {
            stopTimer();
          }
        })
        .catch((error) => {
          console.error(error || NETWORK_REQUIRED);
        })
        .finally(() => setResending(false));
    }
  }, [OTPModalError]);

  const isAllFieldsTouched = useCallback((touched: Touched) => {
    let isTouched = true;
    Object.values(touched || {}).forEach((value) => {
      if (!value) {
        isTouched = false;
      }
    });
    return isTouched;
  }, []);

  const validate = useCallback((values: IOtpPin) => {
    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (e) {
      return { [FORM_ERROR]: 'Please enter the 5 digits OTP' };
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (open) {
      timer.start(2 * 60);
      requestRef.current = requestAnimationFrame(animate);
    } else {
      stopTimer();
    }
    return stopTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reusable content for mobile breakpoint
  const content = (
    <Form
      onSubmit={doSubmit as any}
      validate={validate}
      initialValues={initialValues}
      render={({ handleSubmit, touched, submitting, form, error }) => (
        <form onSubmit={handleSubmit}>
          <span className={styles.message}>
            An OTP has been sent to your mobile number and email. Please enter the 5 digits OTP in the fields below.
          </span>
          <div className={styles.digitsInput}>
            <DigitInput name="digit1" inputRef={digitRefs[0]} />
            <DigitInput name="digit2" inputRef={digitRefs[1]} />
            <DigitInput name="digit3" inputRef={digitRefs[2]} />
            <DigitInput name="digit4" inputRef={digitRefs[3]} />
            <DigitInput name="digit5" inputRef={digitRefs[4]} />
            {/* <DigitInput name="digit6" inputRef={digitRefs[5]} /> */}
          </div>
          {isAllFieldsTouched(touched) && error && <div className={styles.errorMsg}>{error}</div>}
          <div className={styles.resendContainer}>
            {getMinutes(timeLeft)}:{getSeconds(timeLeft)}
            <MuiButton
              color="primary"
              onClick={handleOnResendOtp(form)}
              className={styles.resendButton}
              disabled={resending || !timeIsOver}
            >
              Resend OTP{resending && <CircularProgress size={16} color="inherit" className={styles.spinner} />}
            </MuiButton>
          </div>
          <Button type="submit" theme={ButtonThemes.primary} fullWidth disabled={submitting || loading}>
            {submitting || loading ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
          </Button>
        </form>
      )}
    />
  );

  return (
    <ModalWindow open={open} onClose={handleOnClose} fullScreen>
      <div className={styles.wrapper}>
        <NavigationTitle to="#/" onClick={onClose} layoutType={LayoutType.dashboard} className={styles.navTitle}>
          OTP Verification
        </NavigationTitle>
        {content}
      </div>
    </ModalWindow>
  );
};

export default OTPVerificationModal;
